<template>
  <div>
    <validation-observer ref="studentForm">
      <b-form @submit.prevent="saveStudentEntityForm"
              @reset.prevent="clearForm">
        <div class="vx-row">
          <div class="vx-col sm:w-1/3 w-full">
            <b-form-group
              :label="$t('student.document')"
              label-for="document">
                <validation-provider
                  #default="{ errors }"
                  :name="$t('student.document')"
                  rules="required"
                  class="flex-grow">
                  <b-form-input
                    id="document"
                    v-model="studentEntity.document"
                    :state="errors.length > 0 ? false:null"
                    name="document"
                    class="required"
                    v-mask="['###.###.###-##']"/>
                    <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
            </b-form-group>
          </div>
          <div class="vx-col sm:w-2/3 w-full">
            <validation-provider
              #default="validationContext"
              :name="$t('student.name')"
              rules="required"
              class="flex-grow"
              slim>
              <b-form-group
                :class="[inline ? 'flex-col items-start pb-1 inline-input-group': '']"
                id="input-group-1"
                :label="$t('student.name')"
                label-for="name">
                <b-form-input
                  class="required pr-0 w-full"
                  id="name"
                  type="text"
                  v-model="studentEntity.name"
                  :state="getValidationState(validationContext)"></b-form-input>
                <b-form-invalid-feedback :class="[{'absolute': inline}]">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </div>
        </div>
        <div class="vx-row mt-1">
          <div class="vx-col sm:w-1/2 w-full">
            <validation-provider
              #default="validationContext"
              :name="$t('student.email')"
              rules="email|required"
              class="flex-grow"
              slim>
              <b-form-group
                id="input-group-2"
                :label="$t('student.email')"
                label-for="email"
                :class="[inline ? 'flex-col items-start pb-1 inline-input-group': '']">
                <b-form-input
                  id="email"
                  class="required pr-0 w-full"
                  type="email"
                  v-model="studentEntity.email"
                  :state="getValidationState(validationContext)"
                  :disabled="isEdit()"></b-form-input>
                  <b-form-invalid-feedback :class="[{'absolute': inline}]">
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </div>
          <div class="vx-col sm:w-1/4 w-full">
            <validation-provider
              #default="validationContext"
              :name="$t('student.birthdate')"
              rules="required|min_age"
              class="flex-grow"
              slim>
              <b-form-group
                id="input-group-3"
                :label="$t('student.birthdate')"
                label-for="birthdate"
                :class="[inline ? 'flex-col items-start pb-1 inline-input-group': '']">
                <datepicker
                    id="birthdate"
                    v-model="studentEntity.birthdate"
                    :format="format"
                    :language="ptBR"
                    typeable="true"
                    class="required"
                    v-mask="['##/##/####']"></datepicker>
                  <b-form-invalid-feedback :class="[{'absolute': inline}]">
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
            </validation-provider>
          </div>
        </div>

        <div class="vx-row mt-1">
          <div class="vx-col w-full">
            <b-form-group
              id="description"
              :label="$t('student.description')"
              label-for="description">
              <complex-editor :editor_data.sync="studentEntity.description"/>
            </b-form-group>
          </div>
        </div>

        <div class="vx-row mt-2 mb-2">
          <div class="vx-col w-full flex gap-2 justify-end">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="float-right"
              :disabled="!validateForm"
              type="submit">
              Salvar
            </b-button>
            <b-button type="reset" variant="outline-secondary" class="float-right">
              <slot name="reset-button-text">Limpar</slot>
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="outline-secondary"
              @click="goBack"
              class="float-right mr-2">
              Voltar
            </b-button>
          </div>
        </div>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BFormInput,
  BButton,
  BFormRow,
  BCol,
  BFormInvalidFeedback,
  BInputGroup,
  BInputGroupAppend
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, min, email } from '@validations'
import Ripple from 'vue-ripple-directive'
import Datepicker from 'educatena-vuejs-datepicker'
import { ptBR } from 'educatena-vuejs-datepicker/dist/locale'
import ComplexEditor from '@/components/ComplexEditor.vue'

export default {
  directives: {
    Ripple,
  },
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BFormRow,
    BFormInvalidFeedback,
    BCol,
    ValidationObserver,
    ValidationProvider,
    BInputGroup,
    BInputGroupAppend,
    Datepicker,
    ComplexEditor
  },

  props: {
    studentId: {
      type: Number,
      default: null
    },
    studentEntity: {
      type: Object,
      default: {}
    },
    services: {
      type: Object,
      default: null
    },
    showSubmit: {
      type: Boolean,
      default: true
    },
    showReset: {
      type: Boolean,
      default: true
    },
    inline: {
      type: Boolean,
      default: false
    },
    inlineInput: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    required, email, min,
    format: 'dd/MM/yyyy',
    ptBR
  }),

  methods: {
    saveStudentEntityForm() {
      this.$vs.loading()

      const modelData = {
        id: this.studentEntity.id,
        name: this.studentEntity.name,
        document: this.studentEntity.document,
        birthdate: this.studentEntity.birthdate,
        email: this.studentEntity.email,
        status: this.studentEntity.status,
        description: this.studentEntity.description
      }

      this.services.studentService.createOrUpdate(modelData).then(
        data => {
          this.$vs.loading.close()
          this.notifySuccess(this.$vs)
          this.studentEntity.id = data.id
          this.studentEntity.people_id = data.people_id
          this.$emit('update-data', this.studentEntity)
        },
        error => {
          this.$vs.loading.close()
          this.showErrors(this.$validator, error, this)
          this.showErrors(this.$refs.studentForm, error)
        }
      ).finally(() => { this.$vs.loading.close() })
    },
    clearForm() {
      this.$emit('reset')
      this.studentEntity.name = ''
      this.studentEntity.document = ''
      this.studentEntity.birthdate = ''
      this.studentEntity.email = ''
      this.studentEntity.description = ''
      this.clearValidationErrors()
    },
    goBack() {
      this.$router.push('/students')
    },
    isEdit() {
      return !_.isNil(this.studentId)
    },
    getValidationState({ invalid, validated, ...obj }){
      const valid = !invalid
      return (
        !validated ? null : valid
      )
    },
    showValidationErrors(error) {
      this.showErrors(this.$refs.validator, error)
    },
    clearValidationErrors() {
      this.$refs.studentForm.reset()
    }
  },
  computed: {
    validateForm() {
      return (
        !this.isEmpty(this.studentEntity.name) &&
        !this.isEmpty(this.studentEntity.email)
      )
    },
  },
  watch: {
  },
  beforeMount() {

  },
  mounted() {

  }
}
</script>

<style lang="scss">
  .inline-input-group > div:nth-child(2) {
    width: 100%;
  }
</style>

